/**
 * Logout Page
 *
 * Professional logout confirmation page with security assurance and easy re-login.
 *
 * Key Features:
 * - Clear confirmation message ("You've been logged out")
 * - Security assurance (session terminated messaging)
 * - One-click "Log Back In" button
 * - Auto-redirect to /login after 10 seconds
 * - Visual countdown for user awareness
 *
 * UX Rationale (from Inara):
 * - Debra gets interrupted constantly, may accidentally logout - needs confirmation
 * - Doug handles sensitive pricing data - wants explicit session termination messaging
 * - Keeps users in app context (valuable for multi-org switching)
 * - Professional messaging reinforces trust
 */

import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Result, Button, Typography, Spin } from 'antd'
import { CheckCircleOutlined } from '@ant-design/icons'
import authService from '@/services/authService'
import { useCurrentUser } from '@/hooks'
import { featureTierService } from '@/services/featureTierService'
import config from '@/config/environments'

const { Text } = Typography

export const LogoutPage: React.FC = () => {
  const navigate = useNavigate()
  const [countdown, setCountdown] = useState(10)
  const [isLoggingOut, setIsLoggingOut] = useState(false)

  // Check if user is authenticated
  const { data: user } = useCurrentUser()

  useEffect(() => {
    // If user is authenticated, perform logout
    const performLogout = async () => {
      if (user && !isLoggingOut) {
        setIsLoggingOut(true)

        // For rp_admin/rp_support users, clear org context before logout
        // This prevents them from accidentally being in a client's org on next login
        if (featureTierService.hasInternalAccess(user)) {
          console.log('🔴 LOGOUT: Clearing org context for internal user...')
          try {
            const clearResponse = await fetch(`${config.authApiBaseUrl}/me/orgs/clear`, {
              method: 'POST',
              credentials: 'include',
              headers: { 'Content-Type': 'application/json' }
            })
            if (clearResponse.ok) {
              console.log('🔴 LOGOUT: Org context cleared')
            } else {
              console.warn('🔴 LOGOUT: Clear org returned non-OK status:', clearResponse.status)
            }
          } catch (clearError) {
            console.warn('🔴 LOGOUT: Failed to clear org context:', clearError)
          }
        }

        // Clear session storage hint
        sessionStorage.removeItem('isAuthenticated')

        // Clear local storage
        localStorage.removeItem('cognito-auth-tokens')
        localStorage.removeItem('oauth-state')

        // Sign out with Hosted UI (redirects to Cognito, then back)
        await authService.signOutWithHostedUI()
      }
    }

    performLogout()
  }, [user, isLoggingOut])

  useEffect(() => {
    // Only start countdown if user is NOT authenticated (logout completed)
    if (!user && !isLoggingOut) {
      const timer = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            navigate('/login')
            return 0
          }
          return prev - 1
        })
      }, 1000)

      // Cleanup function to clear interval if component unmounts
      return () => clearInterval(timer)
    }

    // Return no-op cleanup function when timer is not active
    return () => {}
  }, [navigate, user, isLoggingOut])

  const handleLoginClick = () => {
    navigate('/login')
  }

  // Show loading spinner while logging out
  if (user || isLoggingOut) {
    return (
      <div
        style={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'var(--rp-gradient-background)',
          padding: '24px',
        }}
      >
        <div style={{ textAlign: 'center' }}>
          <Spin size="large" />
          <div style={{ marginTop: 16 }}>
            <Text type="secondary">Logging out...</Text>
          </div>
        </div>
      </div>
    )
  }

  // Show confirmation message after logout
  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'var(--rp-gradient-background)',
        padding: '24px',
      }}
    >
      <div style={{ maxWidth: 600, width: '100%' }}>
        <Result
          status="success"
          icon={<CheckCircleOutlined style={{ color: 'var(--rp-success)' }} />}
          title="You've been logged out"
          subTitle="Your session has been securely terminated. Come back soon!"
          extra={[
            <Button
              key="login"
              type="primary"
              size="large"
              onClick={handleLoginClick}
              style={{ minHeight: 48, fontSize: 16 }}
            >
              Log Back In
            </Button>,
            <div key="countdown" style={{ marginTop: 16 }}>
              <Text type="secondary">
                Redirecting to login in {countdown} second{countdown !== 1 ? 's' : ''}...
              </Text>
            </div>,
          ]}
        />
      </div>
    </div>
  )
}

export default LogoutPage
